// A part's price history in its panel (ERP-PLAN §5.13 "Part panel → Cost"): every quote price as it changed and every
// sent PO line's price (price_point, written by the server), as a sparkline with the planned price beside it, the last
// price paid, and a flag when that's more than 5% over plan. Each point explains itself on hover.
import { useMemo } from "react";
import { PRICE_FLAG_PCT, priceHistory } from "../../../../../shared/costs.ts";
import type { Item } from "../../../../../shared/db.gen.ts";
import { shortDate, usd4, useCostIndex } from "../../../lib/costs.ts";
import { Badge } from "../../../ui/controls.tsx";
import { Section } from "../../../ui/RecordPanel.tsx";
import { Why } from "../../../ui/Why.tsx";

const W = 300;
const H = 56;

export function PriceHistorySection({ item }: { item: Item }) {
  const cx = useCostIndex();
  const h = useMemo(() => priceHistory(cx, item.id), [cx, item.id]);
  if (!h.points.length && h.planned.unit == null) return null;
  const vendorName = (id: string | null) => {
    const v = id ? cx.vendorById.get(id) : undefined;
    return v ? v.short_name || v.name : "";
  };
  const planned = h.planned.unit;
  const paid = h.lastPaid ? Number(h.lastPaid.unit_price) : null;
  const over = planned && paid != null ? ((paid - planned) / planned) * 100 : null;
  const values = [...h.points.map((p) => Number(p.unit_price)), ...(planned != null ? [planned] : [])];
  const lo = Math.min(...values) * 0.95;
  const hi = Math.max(...values) * 1.05 || 1;
  const span = Math.max(1e-9, hi - lo);
  const times = h.points.map((p) => new Date(`${String(p.at).slice(0, 10)}T12:00:00Z`).getTime());
  const t0 = Math.min(...times);
  const t1 = Math.max(...times);
  // By date; points on the same day (or all on one) step apart in order so each can be seen and hovered.
  const n = h.points.length;
  const xs = times.map((t, i) => (t1 === t0 ? (n === 1 ? W / 2 : 6 + (i / (n - 1)) * (W - 12)) : 6 + ((t - t0) / (t1 - t0)) * (W - 12)));
  for (let i = 1; i < n; i++) if (xs[i] - xs[i - 1] < 12) xs[i] = Math.min(W - 6, xs[i - 1] + 12);
  const x = (_t: number, i: number) => xs[i];
  const y = (v: number) => H - 6 - ((v - lo) / span) * (H - 12);
  const path = h.points.map((p, i) => `${i ? "L" : "M"}${x(times[i], i).toFixed(1)},${y(Number(p.unit_price)).toFixed(1)}`).join(" ");
  return (
    <Section title="Price history" count={h.points.length}>
      <div className="px-2">
        {h.points.length > 0 && (
          <div className="relative" style={{ width: W, height: H }}>
            <svg width={W} height={H} className="absolute inset-0 overflow-visible" role="img" aria-label={`${item.sku} price history`}>
              {planned != null && <line x1={0} x2={W} y1={y(planned)} y2={y(planned)} stroke="var(--line-strong)" strokeWidth={1} />}
              <path d={path} fill="none" stroke="var(--series-1)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
            </svg>
            {h.points.map((p, i) => (
              <span key={p.id} className="absolute" style={{ left: x(times[i], i) - 10, top: y(Number(p.unit_price)) - 10 }}>
                <Why
                  title={`${usd4(Number(p.unit_price))} ${p.currency !== "USD" ? p.currency : ""}`}
                  lines={[
                    { label: p.source === "po" ? `On ${p.ref}` : `Quote${p.ref ? ` ${p.ref}` : ""}`, value: shortDate(String(p.at)) },
                    { label: vendorName(p.vendor_id) || "Vendor", value: p.qty ? `${Number(p.qty).toLocaleString("en-US")} units` : "" },
                  ]}
                  width={240}
                  className="flex size-5 items-center justify-center"
                >
                  <span
                    className="block size-2 rounded-full ring-2 ring-[var(--surface)]"
                    style={{ background: p.source === "po" ? "var(--series-1)" : "var(--surface)", border: "2px solid var(--series-1)" }}
                  />
                </Why>
              </span>
            ))}
          </div>
        )}
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px]">
          <span>
            <span className="text-muted">Planned </span>
            <Why title="Planned price" note={h.planned.text}>
              <span className="font-mono">{usd4(planned)}</span>
            </Why>
          </span>
          {h.lastPaid && (
            <span>
              <span className="text-muted">Last paid </span>
              <span className="font-mono">{usd4(paid)}</span>
              <span className="text-faint">
                {" "}
                on {h.lastPaid.ref}, {shortDate(String(h.lastPaid.at))}
              </span>
            </span>
          )}
          {over != null && over > PRICE_FLAG_PCT && (
            <Badge tone="warn">
              +{Math.round(over)}% on {h.lastPaid?.ref}
            </Badge>
          )}
        </div>
        <div className="mt-0.5 text-[11px] text-faint">Filled dots: prices on sent POs · hollow: quotes · grey line: the planned price</div>
      </div>
    </Section>
  );
}
